<template>
  <BlockFieldset class="project-measurement-point-operator">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <b-form-group
        horizontal
        :label="$t('measurementPointOperator')"
        label-for="measurementPointOperator"
        label-class="text-md-right"
        :label-cols="4"
        breakpoint="md"
      >
        <multiselect
          :placeholder="$t('powerCompany')"
          v-model="project.measurementPointOperator"
          id="measurementPointOperator"
          :options="measurementPointOperatorOptions"
          :allow-empty="true"
          :show-labels="false"
          track-by="name"
          label="name"
          :disabled="isBlockReadOnly"
        >
          <template slot="singleLabel" slot-scope="{ option }">
            {{ option.name }} - {{ option.msbNumber }}
          </template>
          <template slot="option" slot-scope="{ option }">
            {{ option.name }} - {{ option.msbNumber }}
          </template>
        </multiselect>
      </b-form-group>

      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('measurementPointOperator') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { toProjectMeasurementPointOperatorInput } from './projectMeasurementPointOperatorInput';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-measurement-point-operator ("Messstellenbetreiber", see feature page projekt-evu):
 * the operator of the project, shown as "name - msbNumber" and clearable as before. The editor
 * loads the operator list itself (the block brings the read on `editor`); a reader sees only the
 * selected one. On the unsaved project it shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectMeasurementPointOperator',
  components: { BlockFieldset, Multiselect },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  computed: {
    ...mapGetters(['getMeasurementPointOperators']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    blockInput() {
      return toProjectMeasurementPointOperatorInput(this.project);
    },
    measurementPointOperatorOptions() {
      return this.isBlockReadOnly ? [] : this.getMeasurementPointOperators;
    },
  },
  methods: {
    ...mapActions([
      'updateProjectMeasurementPointOperator',
      'fetchMeasurementPointOperators',
      'message',
    ]),
    saveBlockInput(input) {
      return this.updateProjectMeasurementPointOperator({
        projectNumber: this.project.number,
        input,
      });
    },
    /** A failed fetch leaves the select empty; the block stays usable with the stored value. */
    async loadMeasurementPointOperators() {
      try {
        await this.fetchMeasurementPointOperators();
      } catch (error) {
        console.error('Error loading measurement point operators:', error);
        this.message({ message: error.message, type: 'danger', lifetime: 4 });
      }
    },
  },
  mounted() {
    if (this.isNewProject || this.isBlockReadOnly) {
      return;
    }
    this.loadMeasurementPointOperators();
  },
};
</script>
